import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import PageHero from '@/components/feature/PageHero';
import CourseCard, { type CourseItem } from '@/components/feature/CourseCard';
import CourseOutline from '@/pages/courses/detail/components/CourseOutline';
import PurchaseQrModal from '@/components/feature/PurchaseQrModal';
import ConsultQrModal from '@/components/feature/ConsultQrModal';
import { courses, hospitalCourses, doctors } from '@/mocks/home';
import { fetchCourseInfo, fetchCourseList } from '@/services/course';
import { adaptCourseDetail, adaptCourses, type CourseDetailItem } from '@/services/adapters';
import { useRequest } from '@/hooks/useRequest';

/** 课程亮点默认内容（后端未配置时回退展示） */
const defaultHighlights = [
  { icon: 'ri-book-open-line', title: '体系化课程', desc: '由浅入深、分层递进，构建完整的知识框架。' },
  { icon: 'ri-stethoscope-line', title: '临床导向', desc: '紧扣临床真实场景，学完即可用于诊疗实践。' },
  { icon: 'ri-award-line', title: '名师亲授', desc: '名老中医与骨干医师全程讲解，答疑解惑。' },
  { icon: 'ri-time-line', title: '随时回看', desc: '购买后可反复观看，碎片时间也能持续学习。' },
];

/** 适合人群默认内容（后端未配置时回退展示） */
const defaultAudiences = ['基层医疗机构中西医结合医师', '中医临床骨干与住院医师', '中医药院校师生与传承人', '关注健康的养生爱好者'];

const allMockCourses: CourseItem[] = [...courses, ...hospitalCourses];

export default function CourseDetailPage() {
  const { id } = useParams();
  const courseId = Number(id);
  const [buying, setBuying] = useState<CourseItem | null>(null);
  const [consultOpen, setConsultOpen] = useState(false);

  // 课程详情（接口失败时降级为 mock 数据）
  const { data: remoteCourse } = useRequest(
    () => fetchCourseInfo(courseId).then((res) => adaptCourseDetail(res.dataInfo)),
    { fallback: null as CourseDetailItem | null },
  );

  // 相关课程推荐（接口失败时降级为 mock 数据）
  const { data: relatedList } = useRequest(
    () => fetchCourseList({ pageIndex: 1, pageSize: 5 }).then((res) => adaptCourses(res.list)),
    { fallback: allMockCourses.slice(0, 5) },
  );

  const mockCourse = allMockCourses.find((item) => item.id === courseId) ?? null;
  const mockTeacher = mockCourse ? doctors.find((item) => item.name === mockCourse.doctor) : undefined;

  const course: CourseDetailItem | null =
    remoteCourse ??
    (mockCourse
      ? {
          ...mockCourse,
          description: '',
          totalDuration: '',
          highlights: [],
          audiences: [],
          teacher: mockTeacher
            ? {
                name: mockTeacher.name,
                title: mockTeacher.title,
                hospital: mockTeacher.hospital,
                intro: mockTeacher.intro,
                avatar: mockTeacher.avatar,
              }
            : null,
        }
      : null);

  if (!course) {
    return (
      <PageShell>
        <PageHero
          eyebrow="课程中心"
          title="课程不存在"
          description="抱歉，未找到该课程，可能已下架或链接有误。"
          breadcrumb={[{ label: '首页', to: '/' }, { label: '名师课程', to: '/courses/master' }, { label: '课程详情' }]}
        />
        <section className="w-full px-4 md:px-8 py-16 text-center">
          <Link
            to="/courses/master"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回课程中心
          </Link>
        </section>
      </PageShell>
    );
  }

  const related = relatedList.filter((item) => item.id !== courseId).slice(0, 4);
  // 归属频道：真实数据按课程类型判断（1-名师 2-名院）；类型未知（缺字段/历史 0 值）时按名院课程列表兜底判断
  const isHospitalCourse =
    course.courseType === 2 ||
    (course.courseType !== 1 && hospitalCourses.some((item) => item.id === course.id));
  const listPath = isHospitalCourse ? '/courses/hospital' : '/courses/master';
  const listLabel = isHospitalCourse ? '名院课程' : '名师课程';
  const teacher = course.teacher;
  // 亮点/人群优先用后端配置，未配置时回退默认内容
  const courseHighlights = course.highlights.length > 0 ? course.highlights : defaultHighlights;
  const courseAudiences = course.audiences.length > 0 ? course.audiences : defaultAudiences;

  const statItems = [
    { label: '有效期', value: '2 年' },
    // 主讲老师未设置时隐藏该项
    ...(course.doctor ? [{ label: '主讲老师', value: course.doctor }] : []),
    { label: '课时数', value: `${course.lessons} 课时` },
    { label: '总时长', value: course.totalDuration || `${course.lessons * 30} 分钟` },
  ];

  return (
    <PageShell>
      <PageHero
        eyebrow="课程中心"
        title={course.title}
        breadcrumb={[{ label: '首页', to: '/' }, { label: listLabel, to: listPath }, { label: '课程详情' }]}
      />

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <div className="w-full h-[260px] md:h-[380px] rounded-lg overflow-hidden border border-background-200">
              <img
                src={course.image}
                alt={course.title}
                title={`${course.title} - 精品课程`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* 课程数据 */}
            <div
              className={`mt-5 grid grid-cols-2 rounded-lg border border-background-200 overflow-hidden divide-x divide-background-200 ${
                statItems.length >= 4 ? 'sm:grid-cols-4' : 'sm:grid-cols-3'
              }`}
            >
              {statItems.map((stat) => (
                <div key={stat.label} className="flex flex-col items-center justify-center py-4 px-2 bg-background-50">
                  <span className="font-heading text-lg font-bold text-foreground-900">{stat.value}</span>
                  <span className="mt-1 text-xs text-foreground-500">{stat.label}</span>
                </div>
              ))}
            </div>

            <h2 className="mt-8 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              课程介绍
            </h2>
            {course.description ? (
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">{course.description}</p>
            ) : (
              <>
                <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  《{course.title}》{course.doctor ? `由 ${course.doctor} 老师主讲，` : ''}共 {course.lessons} 课时，围绕“{course.category}”方向系统展开。
                  课程以临床真实需求为出发点，将中医辨证思路与现代医学方法有机融合，帮助学员建立清晰、可复用的诊疗路径。
                </p>
                <p className="mt-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                  每节课均配有知识要点、典型医案与实操演示，学完即可应用于日常诊疗，并可通过课后练习巩固提升。
                </p>
              </>
            )}

            {/* 主讲老师未设置（teacher 为空）时整块隐藏 */}
            {teacher && (
              <>
                <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
                  <span className="w-1.5 h-5 rounded-full bg-secondary-500"></span>
                  主讲老师
                </h2>
                <div className="mt-4 flex flex-col sm:flex-row gap-5 p-5 rounded-lg bg-background-100 border border-background-200">
                  <div className="shrink-0 w-24 h-24 mx-auto sm:mx-0 rounded-lg overflow-hidden">
                    {teacher.avatar ? (
                      <img
                        src={teacher.avatar}
                        alt={teacher.name}
                        title={`${teacher.name} - 主讲老师`}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-secondary-100 text-secondary-700">
                        <i className="ri-user-line text-3xl"></i>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-bold text-foreground-900">{teacher.name}</h3>
                      <span className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                        {teacher.title || '主讲老师'}
                      </span>
                    </div>
                    {teacher.hospital && (
                      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-foreground-500">
                        <i className="ri-hospital-line"></i>
                        {teacher.hospital}
                      </p>
                    )}
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                      {teacher.intro ||
                        `${teacher.name} 老师长期从事临床与教学一线工作，理论与实操并重，讲课深入浅出、案例丰富，深受学员好评。`}
                    </p>
                  </div>
                </div>
              </>
            )}

            <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
              <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
              课程亮点
            </h2>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {courseHighlights.map((item) => (
                <div
                  key={item.title}
                  className="flex gap-4 p-5 rounded-lg bg-background-100 border border-background-200"
                >
                  <span className="shrink-0 w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1">{item.title}</h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <CourseOutline courseId={courseId} />

            <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
              <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
              适合人群
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {courseAudiences.map((item) => (
                <span key={item} className="px-3.5 py-2 rounded-full bg-secondary-100 text-secondary-900 text-sm">
                  {item}
                </span>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {course.category}
              </span>
              {course.price > 0 ? (
                <div className="mt-4 flex items-baseline gap-1 font-heading font-bold text-primary-600">
                  <span className="text-lg">¥</span>
                  <span className="text-3xl">{course.price}</span>
                </div>
              ) : (
                <div className="mt-4 font-heading font-bold text-primary-600">
                  <span className="text-3xl">免费</span>
                </div>
              )}

              <div className="mt-5 space-y-3 text-sm text-foreground-700">
                {course.doctor && (
                  <div className="flex items-center justify-between">
                    <span className="text-foreground-500">主讲老师</span>
                    <span className="font-medium text-foreground-900">{course.doctor}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500">课程总课时</span>
                  <span className="font-medium text-foreground-900">{course.lessons} 课时</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-foreground-500">学习方式</span>
                  <span className="font-medium text-foreground-900">在线视频 · 随时回看</span>
                </div>
              </div>

              {/* 免费课程不展示购买按钮与支付提示 */}
              {course.price > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setBuying(course)}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shopping-bag-3-line"></i>
                    立即购买
                  </button>
                  <p className="mt-2.5 flex items-center justify-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-wechat-line text-accent-600"></i>
                    微信扫码进入小程序完成支付
                  </p>
                </>
              )}
              <button
                type="button"
                onClick={() => setConsultOpen(true)}
                className={`${course.price > 0 ? 'mt-3' : 'mt-6'} w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-primary-200 text-primary-600 hover:bg-primary-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap`}
              >
                <i className="ri-customer-service-2-line"></i>
                课程咨询
              </button>

              <div className="mt-5 pt-5 border-t border-background-200 space-y-2 text-xs text-foreground-500">
                <p className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-accent-600"></i>
                  正版授权 · 品质保障
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-refund-2-line text-accent-600"></i>
                  7 天内可申请退款
                </p>
              </div>
            </div>
          </aside>
        </div>

        <div className="max-w-7xl mx-auto mt-14 md:mt-20">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">相关课程推荐</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {related.map((item) => (
              <CourseCard key={item.id} course={item} onBuy={setBuying} />
            ))}
          </div>
        </div>
      </section>

      <PurchaseQrModal
        open={buying !== null}
        onClose={() => setBuying(null)}
        courseTitle={buying?.title ?? ''}
        price={buying?.price ?? 0}
      />

      <ConsultQrModal
        open={consultOpen}
        onClose={() => setConsultOpen(false)}
        label="课程咨询"
        heading="扫码添加课程顾问"
        subtitle="课程详情、开课安排、团体报名等问题，都可随时咨询"
        qrHint="请使用微信扫一扫，添加专属课程顾问"
        notice="咨询时间为工作日 9:00 - 18:00，我们将在收到消息后尽快回复"
        steps={[
          { icon: 'ri-qr-scan-2-line', label: '微信扫码' },
          { icon: 'ri-user-add-line', label: '添加客服' },
          { icon: 'ri-chat-3-line', label: '课程咨询' },
        ]}
      />
    </PageShell>
  );
}